/**
 * AccountForm — post-OTP first-fill of `name` + `email`.
 *
 * Used in two places:
 *   - `/onboarding/account` route (regular sign-in path).
 *   - Embedded inline on `/join/<token>` (team-invite path).
 *
 * Both consumers pass an `onComplete` callback that handles whatever
 * comes next (navigate to /onboarding/workspace, navigate to /, call
 * acceptInvite, etc.). The form itself only knows about the
 * `identity.completeProfile` mutation.
 *
 * Idempotent: calling with values matching the current row succeeds
 * with `changed: false` so re-runs (route guard re-fires) don't paint
 * an error toast.
 *
 * Defaults are seeded from the current `me` so users who have one
 * field filled already (rare — usually a leftover from a previous
 * partial flow) don't have to re-type.
 *
 * @see ADR-0011 §3.5 — post-OTP profile completion
 * @see 08-system-decisions.md §1 — phone is the credential, name+email
 *      are the contactable identity
 */
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Card, CardBody, Form, Submit, SubmitProbe, TextField } from "@flint/ui";
import { identity } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryKey, type Me } from "../../../lib/session.ts";

type FormValues = z.infer<typeof identity.completeProfileInput>;

export interface AccountFormProps {
  /**
   * Optional `me` snapshot from `useQuery(meQueryOptions)`. Used to:
   *  - prefill `name` if it's already non-placeholder.
   *  - prefill `email` if it's already set.
   * Pass `null` to start from blank (anonymous → just verified case).
   */
  me?: Me;
  /** Called after a successful save (or idempotent no-op). */
  onComplete: (result: { name: string; email: string; changed: boolean }) => void;
  /** Submit button copy. Defaults to "Continue". */
  submitLabel?: string;
  /** Pending-state submit copy. Defaults to "Saving". */
  pendingLabel?: string;
}

export function AccountForm({
  me,
  onComplete,
  submitLabel = "Continue",
  pendingLabel = "Saving",
}: AccountFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(identity.completeProfileInput),
    defaultValues: {
      // Only prefill name if it's not the phone placeholder. The
      // server's `isProfileComplete` check uses the same rule, so this
      // keeps the SPA aligned without re-implementing it.
      name: me?.user && me.user.name !== me.user.phone ? me.user.name : "",
      email: me?.user.email ?? "",
    },
  });

  // Re-sync defaults if the `me` snapshot lands after the first render
  // (e.g. the form mounts during a refetch). RHF's `reset` only fires
  // when values genuinely differ, so this is safe to run on every me
  // change.
  useEffect(() => {
    form.reset({
      name: me?.user && me.user.name !== me.user.phone ? me.user.name : "",
      email: me?.user.email ?? "",
    });
  }, [me?.user.id, me?.user.name, me?.user.email, me?.user.phone, form]);

  const completeProfile = trpc.identity.completeProfile.useMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    const result = await completeProfile.mutateAsync(values);
    // Refetch (not just invalidate) so the route guard sees the
    // updated `profileComplete` BEFORE we navigate. Mirrors the
    // pattern in `onboarding/otp-form.tsx`.
    await queryClient.refetchQueries({ queryKey: meQueryKey });
    onComplete({ name: result.name, email: result.email, changed: result.changed });
  });

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-3.5">
              <TextField<FormValues>
                name="name"
                label="Your name"
                helper="Shown on invoices, payslips, and team mentions."
                autoComplete="name"
                autoFocus
              />
              <TextField<FormValues>
                name="email"
                label="Work email"
                helper="Used for receipts, audit trails, and recovery. Phone stays your sign-in credential."
                type="email"
                autoComplete="email"
                inputMode="email"
              />
              <Submit pendingLabel={pendingLabel} disabled={completeProfile.isPending}>
                {submitLabel}
              </Submit>
              {completeProfile.error ? (
                <Alert
                  intent="bad"
                  title="Couldn't save your profile"
                  description={completeProfile.error.message}
                />
              ) : null}
            </CardBody>
          </Card>
        </form>
      </SubmitProbe>
    </Form>
  );
}
